import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import CTABand from "@/components/CTABand";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Container } from "@/components/Reveal";
import { getGlossaryTerm, getArticle, GLOSSARY } from "@/data/education";
import { SERVICES, COMPANY } from "@/data/content";

export default function GlossaryTerm() {
  const { term: termSlug } = useParams();
  const term = getGlossaryTerm(termSlug);
  if (!term) return <Navigate to="/education/glossary" replace />;

  const relatedArticles = (term.related?.articles || []).map(getArticle).filter(Boolean);
  const relatedServices = (term.related?.services || [])
    .map((s) => SERVICES.find((x) => x.slug === s))
    .filter(Boolean);
  const otherTerms = GLOSSARY.filter((g) => g.slug !== term.slug).slice(0, 8);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: term.term,
    description: term.definition,
    inDefinedTermSet: `https://${COMPANY.domain}/education/glossary`,
  };

  return (
    <>
      <SEO
        title={`${term.term} — Glossary`}
        description={term.short}
        path={`/education/glossary/${term.slug}`}
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden bg-navy pt-[74px] text-white md:pt-[88px]">
        <div className="grid-texture pointer-events-none absolute inset-0 opacity-50" />
        <Container className="relative py-16 md:py-20">
          <Breadcrumbs
            crumbs={[
              { label: "Education Center", path: "/education" },
              { label: "Glossary", path: "/education/glossary" },
              { label: term.term, path: `/education/glossary/${term.slug}` },
            ]}
            dark
          />
          <div className="mt-8 max-w-3xl">
            <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-energy">
              {term.group}
            </span>
            <h1 className="mt-4 font-heading text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl">
              {term.term}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-white/70">{term.short}</p>
          </div>
        </Container>
      </section>

      <section className="bg-white py-20 md:py-24">
        <Container>
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-8">
              <div className="max-w-3xl space-y-10">
                <div>
                  <h2 className="font-heading text-2xl font-semibold tracking-tight text-navy">
                    Definition
                  </h2>
                  <p className="mt-4 text-xl leading-relaxed text-navy/80">{term.definition}</p>
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-semibold tracking-tight text-navy">
                    Detailed explanation
                  </h2>
                  <p className="mt-4 text-lg leading-relaxed text-navy/75">{term.explanation}</p>
                </div>
                <div className="border-l-2 border-electric bg-ice p-6">
                  <h2 className="font-heading text-xl font-medium text-navy">Why it matters</h2>
                  <p className="mt-3 leading-relaxed text-navy/80">{term.whyItMatters}</p>
                </div>

                <div className="border-t border-line pt-8">
                  <Link
                    to="/education/glossary"
                    className="inline-flex items-center gap-2 text-sm font-medium text-slatey transition-colors hover:text-navy"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back to glossary
                  </Link>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 space-y-10">
                {relatedArticles.length > 0 && (
                  <div>
                    <h3 className="font-mono text-[11px] uppercase tracking-[0.16em] text-slatey">
                      Related Articles
                    </h3>
                    <div className="mt-4 space-y-2">
                      {relatedArticles.map((a) => (
                        <Link
                          key={a.slug}
                          to={`/education/${a.slug}`}
                          data-testid={`term-article-${a.slug}`}
                          className="group flex items-start justify-between gap-3 border-b border-line py-3 text-navy transition-colors hover:text-electric"
                        >
                          <span className="text-sm font-medium leading-snug">{a.title}</span>
                          <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-slatey transition-colors group-hover:text-electric" />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                {relatedServices.length > 0 && (
                  <div>
                    <h3 className="font-mono text-[11px] uppercase tracking-[0.16em] text-slatey">
                      Related Services
                    </h3>
                    <div className="mt-4 space-y-2">
                      {relatedServices.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/solutions/${s.slug}`}
                          className="group flex items-center justify-between border-b border-line py-3 text-navy transition-colors hover:text-electric"
                        >
                          <span className="text-sm font-medium">{s.title}</span>
                          <ArrowUpRight className="h-4 w-4 text-slatey transition-colors group-hover:text-electric" />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>

          {/* Other terms */}
          <div className="mt-20 border-t border-line pt-12">
            <h3 className="font-mono text-[11px] uppercase tracking-[0.16em] text-slatey">
              More terms
            </h3>
            <div className="mt-6 flex flex-wrap gap-3">
              {otherTerms.map((g) => (
                <Link
                  key={g.slug}
                  to={`/education/glossary/${g.slug}`}
                  className="border border-line px-4 py-2.5 text-sm font-medium text-navy transition-colors hover:border-electric hover:text-electric"
                >
                  {g.term}
                </Link>
              ))}
            </div>
          </div>
        </Container>
      </section>

      <CTABand />
    </>
  );
}
